import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useSubscription } from '../contexts/SubscriptionContext';
import { useMembership } from '../hooks/useMembership';
import { Loader2 } from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';

/**
 * Paywall Component
 * Wraps protected content and redirects to /subscription when:
 *   1. The user has no active access at all (trial expired, no Stripe), OR
 *   2. `requireTier` is set and the user's effective membership tier doesn't
 *      meet it (e.g. an Explorer-trial user hitting a Premium-only page).
 *
 * The tier check is layered ON TOP of the legacy subscription/has_access
 * gate so existing grandfathered flows keep working unchanged.
 */
const Paywall = ({ children, requireTier }) => {
  const { hasAccess, loading } = useSubscription();
  const membership = useMembership();
  const { isDarkMode } = useTheme();
  const location = useLocation();

  // Show loading state while checking subscription / membership
  if (loading || membership.loading) {
    return (
      <div className={`min-h-screen flex items-center justify-center ${isDarkMode ? 'bg-slate-950' : 'bg-slate-50'}`}>
        <Loader2 className={`w-8 h-8 animate-spin ${isDarkMode ? 'text-white/50' : 'text-slate-400'}`} />
      </div>
    );
  }

  // No baseline access (trial fully expired, no Stripe) → /subscription
  if (!hasAccess) {
    return <Navigate to="/subscription" replace />;
  }

  // Tier-required check: explorer trial users hitting Premium-only routes
  // are bounced to /subscription with upgrade context.
  if (requireTier === 'premium' && !membership.isPremium) {
    return (
      <Navigate
        to="/subscription"
        replace
        state={{ upgradeRequired: 'premium', from: location.pathname, currentTier: membership.tier }}
      />
    );
  }
  if (requireTier === 'core' && !membership.isCoreOrAbove) {
    return (
      <Navigate
        to="/subscription"
        replace
        state={{ upgradeRequired: 'core', from: location.pathname, currentTier: membership.tier }}
      />
    );
  }

  // User has access, render children
  return children;
};

export default Paywall;
